<script setup>
import { computed, reactive, ref, onMounted, watch } from 'vue'
import { EmptyPage, PageHeading, Input, Icon, Dropdown, Button, Pagination, Checkbox, ProfilePicture, Select } from '@components'

import moment from 'moment'

import { useRoute, useRouter } from 'vue-router'
const route = useRoute()
const router = useRouter()

import { useStaff } from '@store/staff.js'
import { useSettings } from '@store/settings.js'
import { useAppointment } from '@store/appointment.js'

const $staff = useStaff()
const $settings = useSettings()
const $appointment = useAppointment()

onMounted(() => {
    $staff.fetchData()
    $appointment.fetchData()
})

import { sortedDays } from '@utils'

const isAvailableOn = (staff, day = null) => {
    if (staff.meta.status == 'archive' || staff.meta.status == 'draft') return false
    // Currently, we are not using staff working hours.
    // We are using global working hours.

    // staff = {
    //     ...staff,
    //     working_days: $settings.getSettings.working_hours
    // }

    // calculate date, enabled
    if (!day) {
        let date = new Date()
        day = new Date(date).toLocaleDateString('en-US', { weekday: 'long' })
    }

    return [true, 'true'].includes(staff.working_hours[day].enabled)
}

const getSlots = (staff, day = null) => {

    // Currently, we are not using staff working hours.
    // We are using global working hours.

    // staff = {
    //     ...staff,
    //     working_days: $settings.getSettings.working_hours
    // }

    if (!day) {
        let date = new Date()
        day = new Date(date).toLocaleDateString('en-US', { weekday: 'long' })
    }

    const schedule = staff.working_hours[day]
    const slot = schedule?.hours[0] || {}

    if (!slot.start || !slot.end) {
        return 'Not Available'
    }

    return `${moment(slot.start, 'hh:mm a').format('hh:mm a')} - ${moment(slot.end, 'hh:mm a').format('hh:mm a')}`
}

const filter = ref({
    search: '',
    day: 'all',
    status: 'all'
})

const filteredStaffs = computed(() => {
    let filteredArray = $staff.getStaffs
    if (!filteredArray) return []

    // Filter by status
    if (filter.value.status && filter.value.status !== 'all') {
        filteredArray = filteredArray.filter(staff => staff.meta?.status == filter.value.status)
    }

    // Filter by day
    if (filter.value.day && filter.value.day !== 'all') {
        filteredArray = filteredArray.filter(staff => staff.working_hours[filter.value.day].enabled === true)
    }

    // Filter by search
    if (filter.value.search) {
        filteredArray = filteredArray.filter(staff =>
            String(staff.display_name).toLowerCase().includes(filter.value.search.toLowerCase())
            || String(staff.meta.email).toLowerCase().includes(filter.value.search.toLowerCase())
            || String(staff.meta.phone).toLowerCase().includes(filter.value.search.toLowerCase())
            || String(staff.username).toLowerCase().includes(filter.value.search.toLowerCase())
            || String(staff.meta?.bio).toLowerCase().includes(filter.value.search.toLowerCase())
            || String(staff.meta?.title).toLowerCase().includes(filter.value.search.toLowerCase())
            || String(staff.meta?.highlights).toLowerCase().includes(filter.value.search.toLowerCase())
        )
    }

    return filteredArray
})

// Get paginated staffs.
const currentPage = ref(1)
const perPage = ref(10)
const sortBy = ref('staff_id')
const sortDirection = ref('DESC')

const getPaginatedStaffs = computed(() => {
    let _staffs = filteredStaffs.value

    // Sort.
    _staffs = _staffs ? _staffs.sort((a, b) => {
        if (Number(a[sortBy.value]) < Number(b[sortBy.value])) {
            return sortDirection.value == 'ASC' ? -1 : 1
        }

        if (Number(a[sortBy.value]) > Number(b[sortBy.value])) {
            return sortDirection.value == 'ASC' ? 1 : -1
        }

        return 0
    }) : []

    return {
        total: _staffs.length,
        data: _staffs.slice((currentPage.value - 1) * perPage.value, currentPage.value * perPage.value)
    }
})

const subtitle = computed(() => {
    let total = filteredStaffs.value.length

    return `Total ${total} ${total > 1 ? `${String($settings.labels.staff?.plural).toLowerCase()}` : `${String($settings.labels.staff?.singular).toLowerCase()}`}`
})

// Get staff bookings count by date
const appointmentByStaff = (staff_id) => {
    return $appointment.getAppointments?.filter(app => app.staff_id == staff_id) || []
}

const state = reactive({
    allCheckbox: false,
})


const toggleAllStaffs = () => {
    getPaginatedStaffs.value.data.forEach(staff => {
        return staff.meta.checkbox = state.allCheckbox
    })

}

const getSelectedStaffs = computed(() => {
    return getPaginatedStaffs.value.data.filter(staff => staff.meta.checkbox == true) || []
})

watch(getSelectedStaffs, (newValue) => {
    if (newValue.length == 0) {
        state.allCheckbox = false
    }
    else if (newValue.length == perPage.value || newValue.length == getPaginatedStaffs.value.data.length) {
        state.allCheckbox = true
    }
    else {
        state.allCheckbox = -1
    }
})

const updateStaffStatus = async (id, status) => {
    let staff = $staff.getStaffs.find(staff => staff.staff_id == id)
    staff.meta.status = status

    const res = await $staff.updateStaff(staff)

    if (res.success) {
        if (status == 'archive') return Toast.success(`${$settings.labels.staff?.singular} archived successfully`)
        if (status == 'publish') return Toast.success(`${$settings.labels.staff?.singular} restored and published successfully`)
    } else {
        Toast.error(res.message)
    }
}

const archiveStaff = async (id) => {
    let confirm = await Prompt.fire({
        title: `Archive ${String($settings.labels.staff?.singular).toLowerCase()}`,
        message: `Are you sure you want to archive ${String($settings.labels.staff?.singular).toLowerCase()}?`,
        ok: 'Yes, archive',
        cancel: 'No, cancel',
    })
    if (!confirm.ok) return

    await updateStaffStatus(id, 'archive')
}


const publishStaff = async (id) => {
    await updateStaffStatus(id, 'publish')
}

// const cloneStaff = async (id) => {
//     await router.push({ name: 'staff.add', query: { clone_staff: id } })
// }
const dayOptions = computed(() => {
    let days = sortedDays()
    let options = [{ label: 'All Days', value: 'all' }, { label: 'Today', value: moment().format('dddd') }]

    days.map(day => {
        if (day == moment().format('dddd')) return

        options.push({ label: day, value: day })
    })

    return options
})

const statusOptions = computed(() => {
    // note: incomplete requore original array
    return [{ label: `All ${$settings.labels.staff?.plural}`, value: 'all' }, { label: 'Active', value: 'publish' }, { label: 'Archived', value: 'archive' }]
})


const deleteSelected = async () => {
    let ids = getSelectedStaffs.value.map(staff => staff.staff_id)

    let confirm = await Prompt.fire({
        title: `Delete ${ids.length > 1 ? String($settings.labels.staff?.plural).toLowerCase() : String($settings.labels.staff?.singular).toLowerCase()}`,
        message: `Are you sure you want to delete selected ${ids.length > 1 ? ids.length : ''} ${ids.length > 1 ? String($settings.labels.staff?.plural).toLowerCase() : String($settings.labels.staff?.singular).toLowerCase()}?`,
        ok: 'Yes, delete',
        cancel: 'No, cancel',
    })
    if (!confirm.ok) return

    await $staff.bulkDelete(ids)
    $staff.fetchData()
}


const archiveSelected = async () => {
    let ids = getSelectedStaffs.value.map(staff => staff.staff_id)

    let confirm = await Prompt.fire({
        title: `Archive ${ids.length > 1 ? String().toLowerCase() : String($settings.labels.staff?.singular).toLowerCase()}`,
        message: `Are you sure you want to archive selected ${ids.length > 1 ? ids.length : ''} ${ids.length > 1 ? String($settings.labels.staff?.plural).toLowerCase() : String($settings.labels.staff?.singular).toLowerCase()}?`,
        ok: 'Yes, archive',
        cancel: 'No, cancel',
    })
    if (!confirm.ok) return

    await $staff.bulkUpdate(ids, {
        meta: {
            status: 'archive',
        }
    })

    $staff.fetchData()
}


const deleteStaff = async (staff) => {
    const response = await staff.delete()

    if (response.success) {
        Toast.success(`${$settings.labels.staff?.singular} deleted successfully`)
        $staff.fetchData()
    } else {
        // Toast.error(response.message)
    }
}


const copyToClipboard = (content) => {
    const textField = document.createElement("textarea")
    textField.value = content
    document.body.appendChild(textField)
    textField.select()
    document.execCommand("copy")
    textField.remove()
    Toast.success('Copied to clipboard')
}

// Empty page.
const emptyPageContent = {
    heading: {
        title: `You haven’t created any ${String($settings.labels.staff?.singular).toLowerCase()}`,
        subtitle: `To create ${String($settings.labels.staff?.singular).toLowerCase()}, click on the ‘add manually’ button.`
    },
    buttonCta: [
        {
            title: "Add manually",
            subtitle: `Add info about the new ${String($settings.labels.staff?.singular).toLowerCase()} member manually`,
            icon: "userPlus01",
            link: { name: 'staff.add', query: { tab: 'profile' } }
        }
    ]
}


// RouteToStaff.
const RouteToStaff = (staff_id, $event) => {
    // Bail if .prebook-checkbox is closest parent.
    if ($event.target.closest('.prebook-checkbox')) return

    router.push({ name: 'staff.view', params: { staffId: staff_id }, query: { backTo: route.fullPath } })
}
</script>

<template>
    <div class="md:max-w-[992px] m-auto mb-9 relative">

        <div v-if="!$staff.isLoaded" class="absolute top-0 left-0 z-10 flex flex-col w-full gap-3 bg-white fade-out max-h-svh">
            <div class="w-56 h-16 bg-gray-200 rounded-md animate-pulse"></div>
            <div v-for="n in 4" class="w-full h-12 bg-gray-200 rounded-md animate-pulse"></div>
        </div>

        <div v-else>
            <EmptyPage v-if="!$staff.getStaffs.length" :pageContent="emptyPageContent" />

            <div v-else>
                <PageHeading :title="$settings.labels.staff?.plural" :subtitle="subtitle">
                    <template #rightSide>
                        <router-link v-if="access('staff.add')" :to="{ name: 'staff.add', query: { tab: 'profile' } }">
                            <Button size="md" type="tertiary_color" radius="square">Add New
                                <template #before>
                                    <Icon name="plusCircle" />
                                </template>
                            </Button>
                        </router-link>
                    </template>
                </PageHeading>

                <!-- Filter -->
                <div class="flex flex-col md:flex-row md:gap-[52px] my-7 justify-between items-center w-full">
                    <div class="w-full">
                        <Input v-model="filter.search" class="w-full" :placeholder="`Search by ${String($settings.labels.staff?.singular).toLowerCase()} display name, email & phone`" iconLeft="searchLg"></Input>
                    </div>
                    <!-- <Select :options="statusOptions" v-model="filter.status" placeholder="Status" selectionContainer="py-[10px]" class="w-full lg:w-[191px]"></Select> -->
                    <!-- <div class="w-full">
                </div> -->
                    <div class="flex w-full gap-2 mt-2 md:mt-0 md:gap-5 lg:w-auto">
                        <!-- <Select :options="dayOptions" v-model="filter.day" placeholder="Date" selectionContainer="py-[10px]" class="w-full lg:w-[191px]"></Select> -->
                        <Select :options="statusOptions" v-model="filter.status" placeholder="Status" selectionContainer="py-[10px]" class="w-full lg:w-[191px]"></Select>
                    </div>
                </div>
                <!-- Actions -->
                <div class="flex gap-3">
                    <Button v-if="getSelectedStaffs.length" size="md" type="secondary_gray" radius="square" @click.prevent="archiveSelected()" class="mb-5">Move to archive</Button>
                    <Transition name="router-animation">
                        <Button v-if="getSelectedStaffs.length" size="md" type="secondary_gray" radius="square" @click.prevent="deleteSelected()" class="mb-5 border-[#FDA29B] text-[#B42318]">Delete</Button>
                    </Transition>
                </div>

                <!-- Staff List-->
                <!-- Note: unexpected <div> used to create a fancy border -->
                <div class="flex gap-3 overflow-scroll lg:overflow-visible">
                    <!-- Name Column -->
                    <div class="w-full">
                        <div class="text-[#667085] text-sm font-normal bg-[linear-gradient(0deg,#EAECF0,transparent)] p-[0px_1px_1px_1px]">
                            <div class="flex items-center gap-7 py-2.5 px-3 bg-white">
                                <Checkbox v-if="access('staff.add')" v-model="state.allCheckbox" @click="toggleAllStaffs" />
                                <div>Name</div>
                            </div>
                        </div>
                        <div @click.prevent="RouteToStaff(staff.staff_id, $event)" v-for="(staff, index) in getPaginatedStaffs.data" :key="index" class="cursor-pointer h-[60px] flex items-center gap-2.5 py-2.5 px-3 transition-all duration-300 hover:bg-[#F9FAFB] border border-[#EAECF0] border-t-0">
                            <Checkbox v-if="access('staff.add')" v-model="staff.meta.checkbox" />
                            <div class="w-1.5 h-1.5 rounded-full" :style="`background-color: ${staff.meta.color || '#333333'}`"></div>
                            <div class="flex items-center w-full gap-3">
                                <ProfilePicture :name="staff?.name" :img="staff?.meta?.profile_image_url" :bgColor="staff?.meta?.color" class="w-10 h-10" />
                                <div class="text-xs lg:text-sm">
                                    <span class="font-semibold capitalize whitespace-nowrap" :class="staff.meta.status !== 'publish' ? 'text-[#98A2B3]' : 'text-[#344054]'">{{ staff.name }}</span>
                                    <div class="text-[#98A2B3] font-normal whitespace-nowrap">{{ staff.meta.title }}</div>
                                </div>
                                <div class="text-sm font-medium text-[#667085]">{{ staff.usid }}</div>
                            </div>
                            <div class="text-center text-xs font-medium rounded-2xl bg-[#F2F4F7] py-0.5 px-2 capitalize text-[#98A2B3]" v-if="staff.meta.status !== 'publish'">
                                {{ staff.meta.status }}
                            </div>
                            <div class="text-center text-xs font-medium rounded-2xl bg-[#F2F4F7] py-0.5 px-2 text-[#101828]" v-if="staff.meta.is_private">
                                Private
                            </div>
                        </div>
                    </div>

                    <!-- Activity Column -->
                    <!-- <div>
                    <div class="text-[#667085] text-sm font-normal flex items-center">
                        <div v-for="(day, index) in sortedDays()" class="bg-[linear-gradient(0deg,#EAECF0,transparent)] " :class="index + 1 == sortedDays().length ? 'p-[0px_1px_1px_1px]' : 'p-[0px_0px_1px_1px]'">
                            <div class="flex items-center justify-center w-10 h-10 bg-white">{{ day.slice(0, 3) }}</div>
                        </div>

                    </div>
                    <div v-for="(staff, index) in getPaginatedStaffs.data" :key="index" class="h-[60px] flex items-center justify-center border border-[#EAECF0] border-t-0">
                        <div v-for="day in sortedDays()" :key="index" class="w-[40px] h-[60px] flex items-center justify-center">
                            <div class="w-2 h-2 rounded-full" :style="`background-color: ${isAvailableOn(staff, day) ? '#2E90FA' : '#EAECF0'}`"></div>
                        </div>
                    </div>
                </div> -->

                    <!-- Today Column -->
                    <!-- <div>
                    <div class="text-[#667085] text-sm font-normal bg-[linear-gradient(0deg,#EAECF0,transparent)] p-[0px_1px_1px_1px]">
                        <div class="py-2.5 px-4 bg-white">Today</div>
                    </div>
                    <div v-for="(staff, index) in  getPaginatedStaffs.data" :key="index" class="flex items-center justify-center py-2.5 px-4 border border-[#EAECF0] border-t-0 h-[60px] text-xs font-semibold text-nowrap" :class="[isAvailableOn(staff) ? 'text-[#344054]' : 'text-[#F04438]'], { '!text-[#344054] capitalize': staff.meta.status == 'archive' || staff.meta.status == 'draft' }">
                        {{ isAvailableOn(staff)
                            ? getSlots(staff)
                            : staff.meta.status == 'archive' || staff.meta.status == 'draft' ? staff.meta.status : 'On Leave' }}
                    </div>
                </div> -->

                    <!-- Appointments Column -->
                    <div @click.prevent="RouteToStaff(staff.staff_id, $event)">
                        <div class="text-[#667085] text-sm font-normal bg-[linear-gradient(0deg,#EAECF0,transparent)] p-[0px_1px_1px_1px]">
                            <div class="py-2.5 px-4 bg-white capitalize">{{ String($settings.labels.appointment?.singular).toLowerCase() }}</div>
                        </div>
                        <div v-for="( staff, index ) in getPaginatedStaffs.data" :key="index" class="flex items-center justify-center border border-[#EAECF0] border-t-0 h-[60px] py-2.5 px-4 text-[#344054] text-sm font-semibold">
                            {{ appointmentByStaff(staff.staff_id).length || 0 }}
                        </div>
                    </div>

                    <!-- Contact Column -->
                    <div>
                        <div class="text-[#667085] text-sm font-normal bg-[linear-gradient(0deg,#EAECF0,transparent)] p-[0px_1px_1px_1px]">
                            <div class="py-2.5 px-4 bg-white">Contact</div>
                        </div>
                        <div v-for="( staff, index ) in getPaginatedStaffs.data" :key="index" class="flex items-center justify-center gap-3 border border-[#EAECF0] border-t-0 h-[60px] py-2.5 px-4">
                            <Dropdown contentClass="w-56 top-[35px] right-0 w-[130px] z-[9]">
                                <Icon name="phoneCall01" size="24px" :class="staff.status == 'Draft' ? 'text-[#EAECF0]' : 'text-[#98A2B3] transition-all duration-300 hover:text-[#9E77ED]'" />

                                <template #content>
                                    <div class="flex flex-col gap-1">
                                        <div class="rounded-md mx-1.5 my-0.5 transition">
                                            <div @click="copyToClipboard(staff.meta.phone)" class="px-2.5 py-[9px] flex gap-2 items-center hover:bg-[#F9FAFB] rounded-md cursor-pointer transition-all duration-300">
                                                <!-- <Icon size="16px" name="trash01" /> -->
                                                <div class="text-[#344054] text-sm font-medium">Copy Number</div>
                                            </div>
                                            <a :href="staff.meta.phone ? 'tel:' + staff.meta.phone : 'javascript:;'" class="px-2.5 py-[9px] flex gap-2 items-center hover:bg-[#F9FAFB] rounded-md cursor-pointer transition-all duration-300">
                                                <!-- <Icon size="16px" name="trash01" /> -->
                                                <div class="text-[#344054] text-sm font-medium">Call Now</div>
                                            </a>
                                        </div>
                                    </div>
                                </template>
                            </Dropdown>

                            <Dropdown contentClass="w-56 top-[35px] right-0 w-[130px] z-[9]">
                                <Icon name="mail01" size="24px" :class="staff.status == 'Draft' ? 'text-[#EAECF0]' : 'text-[#98A2B3] transition-all duration-300 hover:text-[#9E77ED]'" />

                                <template #content>
                                    <div class="flex flex-col gap-1">
                                        <div class="rounded-md mx-1.5 my-0.5 transition">
                                            <div @click="copyToClipboard(staff.meta.email)" class="px-2.5 py-[9px] flex gap-2 items-center hover:bg-[#F9FAFB] rounded-md cursor-pointer transition-all duration-300">
                                                <!-- <Icon size="16px" name="trash01" /> -->
                                                <div class="text-[#344054] text-sm font-medium">Copy Email</div>
                                            </div>
                                            <a :href="staff.meta.email ? 'mailto:' + staff.meta.email : 'javascript:;'" class="px-2.5 py-[9px] flex gap-2 items-center hover:bg-[#F9FAFB] rounded-md cursor-pointer transition-all duration-300">
                                                <!-- <Icon size="16px" name="trash01" /> -->
                                                <div class="text-[#344054] text-sm font-medium">Send Email</div>
                                            </a>
                                        </div>
                                    </div>
                                </template>
                            </Dropdown>
                            <!-- <a :href="staff.meta.phone ? 'tel:' + staff.meta.phone : 'javascript:;'">
                            <Icon name="phoneCall01" size="24px" :class="staff.status == 'Draft' ? 'text-[#EAECF0]' : 'text-[#98A2B3] hover:text-[#9E77ED]'" />
                        </a> -->
                            <!-- <a :href="staff.meta.email ? 'mailto:' + staff.meta.email : 'javascript:;'">
                            <Icon name="mail01" size="24px" :class="staff.status == 'Draft' ? 'text-[#EAECF0]' : 'text-[#98A2B3] hover:text-[#9E77ED]'" />
                        </a> -->

                            <Dropdown :contentClass="`w-56 top-9 w-[117px] z-[9] ${$settings.isRTL ? ' left-0' : 'right-0'}`">
                                <Icon name="dotsVertical" size="28px" class="text-[#98A2B3] rounded transition-all duration-300 hover:bg-[#F2F4F7]" />

                                <template #content>
                                    <div class="flex flex-col gap-1">
                                        <div class="rounded-md mx-1.5 my-0.5 transition">
                                            <router-link v-if="access('staff.view')" :to="{ name: 'staff.view', params: { staffId: staff.staff_id }, query: { backTo: route.fullPath } }" class="px-2.5 py-[9px] flex gap-2 items-center hover:bg-[#F9FAFB] rounded-md cursor-pointer transition-all duration-300">
                                                <Icon size="16px" name="infoCircle" />
                                                <div class="text-[#344054] text-sm font-medium">Details</div>
                                            </router-link>
                                            <router-link v-if="access('staff.add')" :to="{ name: 'staff.edit', params: { staffId: staff.staff_id }, query: { backTo: route.fullPath } }" class="px-2.5 py-[9px] flex gap-2 items-center hover:bg-[#F9FAFB] rounded-md cursor-pointer transition-all duration-300">
                                                <Icon size="16px" name="edit05" />
                                                <div class="text-[#344054] text-sm font-medium">Edit</div>
                                            </router-link>
                                            <!-- <div @click="cloneStaff(staff.staff_id)" class="px-2.5 py-[9px] flex gap-2 items-center hover:bg-[#F9FAFB] rounded-md cursor-pointer transition-all duration-300">
                                            <Icon size="16px" name="copy01" />
                                            <div class="text-[#344054] text-sm font-medium">Clone</div>
                                        </div> -->
                                            <div v-if="access('staff.add')" :title="staff.meta.status == 'draft' ? 'Cannot publish/archive a draft staff' : ''" @click="staff.meta.status == 'draft' ? '' : staff.meta.status == 'publish' ? archiveStaff(staff.staff_id) : publishStaff(staff.staff_id)" class="px-2.5 py-[9px] flex gap-2 items-center transition-all duration-300 hover:bg-[#F9FAFB] rounded-md" :class="staff.meta.status == 'draft' ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'">
                                                <Icon size="16px" :name="staff.meta.status == 'publish' ? 'archive' : 'check'" />
                                                <div class="text-[#344054] text-sm font-medium">{{ staff.meta.status == 'publish' ? 'Archive' : 'Publish' }}</div>
                                            </div>
                                            <div v-if="access('staff.add')" @click="deleteStaff(staff)" class="px-2.5 py-[9px] flex gap-2 items-center hover:bg-[#F9FAFB] rounded-md cursor-pointer transition-all duration-300">
                                                <Icon size="16px" name="trash01" />
                                                <div class="text-[#344054] text-sm font-medium">Delete</div>
                                            </div>
                                        </div>
                                    </div>
                                </template>
                            </Dropdown>
                        </div>
                    </div>
                </div>
                <div v-if="getPaginatedStaffs.total == 0" class="text-center text-[#b84646] mt-2">
                    No data found
                </div>
                <Pagination v-if="getPaginatedStaffs.total !== 0" class="pt-[20px] overflow-scroll lg:overflow-visible" v-model:currentPage="currentPage" :totalItems="getPaginatedStaffs.total" :itemsPerPage="perPage"></Pagination>
            </div>
        </div>
    </div>
</template>
